import { useEffect, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from 'sonner'
import { Trash2, KeyRound, BookOpen, Copy, Check, AlertTriangle } from 'lucide-react'
import type { ApiKey } from '@/types/account'
import {
  useApiKeysQuery,
  useCreateApiKey,
  useDeleteApiKey,
} from '@/hooks/useApiKeys'
import { VoiceSelector } from '@/components/account/VoiceSelector'
import { AgentIntegrationCard } from '@/components/account/AgentIntegrationCard'
import { useAccountSetting } from '@/hooks/useSetting'
import { useLang } from '@/hooks/useLang'
import { AccountLayout } from './-layout'

export const Route = createFileRoute('/$lang/account/api')({
  component: ApiRoute,
})

function ApiKeysCard() {
  const { t } = useTranslation(['app', 'common'])
  const [name, setName] = useState('')
  const [rawKey, setRawKey] = useState('')
  const [copied, setCopied] = useState(false)
  const createMutation = useCreateApiKey()

  const {
    data: apiKeysData,
    isLoading: isLoadingApiKeys,
    error: apiKeysError,
    refresh: refreshApiKeys,
  } = useApiKeysQuery()

  useEffect(() => {
    if (apiKeysError) toast.error(apiKeysError.message)
  }, [apiKeysError])

  const handleCreate = async () => {
    const trimmedName = name.trim()
    if (!trimmedName) return
    try {
      const response = await createMutation.mutateAsync(trimmedName)
      setRawKey(response.apiKey)
      setName('')
      toast.success(t('apiKeys.created'))
    } catch (err: any) {
      toast.error(err?.message ?? t('apiKeys.createFailed'))
    }
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(rawKey)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error(t('common.copyFailed'))
    }
  }

  const handleDismissKey = () => {
    setRawKey('')
    setCopied(false)
    createMutation.reset()
  }

  return (
    <Card flush>
      <CardHeader>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-1">
            <CardTitle>{t('apiKeys.title')}</CardTitle>
            <p className="text-sm text-muted-foreground">{t('apiKeys.description')}</p>
          </div>
          <div className="flex gap-3 shrink-0">
            <Input
              type="text"
              placeholder={t('apiKeys.namePlaceholder')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate()
              }}
              className="w-52"
            />
            <Button
              onClick={handleCreate}
              disabled={createMutation.isPending || !name.trim()}
              className="shrink-0"
            >
              {createMutation.isPending ? t('common.loading') : t('apiKeys.create')}
            </Button>
          </div>
        </div>
      </CardHeader>

      {/* Newly created key reveal */}
      {rawKey && (
        <CardContent className="pt-0 pb-4 px-6">
          <div className="rounded-lg border border-green-200 bg-green-50 dark:border-green-900/50 dark:bg-green-950/30 p-4 space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                <Check className="h-4 w-4" />
              </div>
              <div className="flex-1 space-y-0.5">
                <h3 className="font-bold text-sm">{t('apiKeys.revealTitle')}</h3>
                <p className="text-xs text-muted-foreground">{t('apiKeys.revealWarning')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded-lg border border-border bg-muted px-4 py-2.5 font-mono text-sm break-all select-all">
                {rawKey}
              </code>
              <Button
                variant="outline"
                size="icon"
                className="shrink-0"
                onClick={handleCopy}
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            <div className="flex justify-end">
              <Button size="sm" onClick={handleDismissKey}>
                {t('apiKeys.done')}
              </Button>
            </div>
          </div>
        </CardContent>
      )}

      {/* Keys list */}
      <CardContent className="p-0">
        {isLoadingApiKeys ? (
          <div className="space-y-0 divide-y divide-border border-t border-border">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-6 py-5">
                <Skeleton className="h-10 w-10 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-3 w-32" />
                </div>
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 w-8 rounded-lg" />
              </div>
            ))}
          </div>
        ) : apiKeysError ? (
          <div className="px-6 pb-6 border-t border-border pt-4">
            <div className="rounded-lg border border-dashed border-border/60 bg-muted/20 py-10 text-center">
              <Button variant="outline" size="sm" onClick={refreshApiKeys}>
                {t('common.retry')}
              </Button>
            </div>
          </div>
        ) : apiKeysData && apiKeysData.items?.length > 0 ? (
          <div className="divide-y divide-border border-t border-border">
            {apiKeysData.items.map((key: ApiKey) => (
              <KeyRow key={key.id} apiKey={key} onDeleted={refreshApiKeys} />
            ))}
          </div>
        ) : (
          <div className="px-6 pb-6 border-t border-border pt-4">
            <div className="rounded-lg border border-dashed border-border/60 bg-muted/20 py-12 text-center text-sm text-muted-foreground">
              {t('apiKeys.noKeys')}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function KeyRow({ apiKey, onDeleted }: { apiKey: ApiKey; onDeleted: () => void }) {
  const { t } = useTranslation(['app', 'common'])
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const deleteMutation = useDeleteApiKey()

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(apiKey.id)
      toast.success(t('apiKeys.removed'))
      onDeleted()
    } catch (err: any) {
      toast.error(err?.message ?? t('apiKeys.removeFailed'))
    }
  }

  if (confirmingDelete) {
    return (
      <div className="flex flex-col gap-3 px-6 py-4 bg-destructive/5 border-l-2 border-destructive">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">{t('apiKeys.removeConfirm')}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              <span className="font-semibold">{apiKey.name}</span>
              <span className="ml-1.5 font-mono">{apiKey.keyPrefix}...</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConfirmingDelete(false)}
            disabled={deleteMutation.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? t('common.loading') : t('apiKeys.remove')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-between px-6 py-4 transition-colors hover:bg-muted/50">
      <div className="flex items-center gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <KeyRound className="h-5 w-5" />
        </div>
        <div>
          <p className="font-semibold">{apiKey.name}</p>
          <p className="font-mono text-xs text-muted-foreground">
            {apiKey.keyPrefix}...
          </p>
        </div>
      </div>
      <div className="flex items-center gap-6">
        <div className="hidden text-right sm:block">
          <p className="text-xs text-muted-foreground">{t('apiKeys.createdAt')}</p>
          <p className="text-sm font-medium">
            {apiKey.createdAt
              ? new Date(apiKey.createdAt).toLocaleDateString()
              : '—'}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
          onClick={() => setConfirmingDelete(true)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}


function ApiRoute() {
  const { t } = useTranslation(['app', 'common'])
  const lang = useLang()

  // Use new useAccountSetting hook for voice
  const { 
    value: agentVoice, 
    setAccountDefault: setVoiceDefault, 
    isUpdating: isUpdatingVoice 
  } = useAccountSetting('call:agent_voice')

  const handleVoiceChange = async (voiceId: string | null) => {
    try {
      await setVoiceDefault(voiceId)
      toast.success(t('settings.saved'))
    } catch (err: any) {
      toast.error(err?.message ?? t('settings.saveFailed'))
    }
  }

  return (
    <AccountLayout>
      <div className="space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* API Keys + Voice - Main content */}
          <div className="lg:col-span-8 space-y-6">
            {/* Agent Integration Card */}
            <AgentIntegrationCard />

            {/* Combined create + list card */}
            <ApiKeysCard />

            {/* Voice Selector */}
            <VoiceSelector
              value={agentVoice}
              onChange={handleVoiceChange}
              disabled={isUpdatingVoice}
            />
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-6">
            {/* Onboarding Guide */}
            <div className="relative overflow-hidden rounded-xl bg-primary p-6 text-primary-foreground shadow-lg">
              <div className="absolute -right-8 -bottom-8 h-40 w-40 rounded-full bg-white/10 blur-3xl" />
              <div className="relative space-y-5">
                <h2 className="text-xl font-bold">{t('apiKeys.gettingStarted')}</h2>
                <p className="text-sm text-primary-foreground/80">
                  {t('apiKeys.gettingStartedDescription')}
                </p>
                <div className="space-y-4 pt-2">
                  <div className="flex gap-3 items-start">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-foreground text-primary text-xs font-bold">
                      1
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{t('apiKeys.step1Title')}</p>
                      <p className="text-xs text-primary-foreground/70 mt-0.5">{t('apiKeys.step1Description')}</p>
                    </div>
                  </div>
                  <div className="flex gap-3 items-start">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-primary-foreground/30 text-xs font-bold">
                      2
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{t('apiKeys.step2Title')}</p>
                      <p className="text-xs text-primary-foreground/70 mt-0.5">{t('apiKeys.step2Description')}</p>
                    </div>
                  </div>
                  <div className="flex gap-3 items-start">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-primary-foreground/30 text-xs font-bold">
                      3
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{t('apiKeys.step3Title')}</p>
                      <p className="text-xs text-primary-foreground/70 mt-0.5">{t('apiKeys.step3Description')}</p>
                    </div>
                  </div>
                </div>
                <Button
                  variant="secondary"
                  className="w-full mt-2"
                  asChild
                >
                  <Link to="/$lang/docs/" params={{ lang }}>
                    <BookOpen className="mr-2 h-4 w-4" />
                    {t('apiKeys.viewDocs')}
                  </Link>
                </Button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </AccountLayout>
  )
}
